import { Link } from "react-router-dom";
import useSeo from "@/lib/useSeo";

const CONTACT_EMAIL = "Gallenhunt90@gmail.com";

export default function About() {
  useSeo({
    title: "About · The Couponer",
    description:
      "Who runs The Couponer, why we built it, and how we curate US coupons and deals every day.",
    canonical: "https://thecouponer.com/about",
  });

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-12" data-testid="about-page">
      <div className="eyebrow text-[10px] text-[#5c5c5c] mb-2">ABOUT US</div>
      <h1 className="serif text-5xl md:text-6xl leading-tight tracking-tight mb-6">
        All the coupons. None of the noise.
      </h1>
      <p className="text-sm text-[#5c5c5c] mb-8">Founded 2026 · Based in the United States</p>

      <div className="max-w-none space-y-6 text-[15px] leading-relaxed">
        <p>
          <strong>The Couponer</strong> is an independent, US-focused almanac of every worthwhile
          promo code, printable coupon, and in-store deal we can get our hands on. We built it
          because every other coupon site had become a maze of pop-ups, expired codes, and
          affiliate spam. We wanted one clean place to actually find deals that work.
        </p>

        <h2 className="serif text-2xl mt-8">Who runs this</h2>
        <p>
          The Couponer is operated by <strong>Gregory Allen Hunter Jr.</strong>, a US-based indie developer.
          You can reach me any time at{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="underline">
            {CONTACT_EMAIL}
          </a>
          . If you find a busted code, spot a great deal we missed, or just want to say hi — the
          inbox is real and I read every email.
        </p>

        <h2 className="serif text-2xl mt-8">How we source deals</h2>
        <p>
          Every day, our ingestion pipeline pulls fresh deals from four sources:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Community-submitted</strong> — signed-in users can submit codes they&apos;ve
            personally verified. Each submission is reviewed before it goes live.
          </li>
          <li>
            <strong>Reddit deal communities</strong> — we monitor public feeds where couponers
            post real-time finds.
          </li>
          <li>
            <strong>Retailer newsletters &amp; blogs</strong> — we track four established coupon
            blogs and syndicate their published deals with attribution.
          </li>
          <li>
            <strong>Editorial picks</strong> — our own team hand-selects the &quot;Deal of the
            Day&quot; each morning and highlights limited-time offers on the front page.
          </li>
        </ul>

        <h2 className="serif text-2xl mt-8">How we make money</h2>
        <p>
          We&apos;re transparent about this because you deserve to know. The Couponer earns revenue
          through four channels:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Affiliate commissions</strong> — some outbound links to retailers include an
            affiliate tag. If you buy something after clicking, we may earn a small commission at
            no extra cost to you.
          </li>
          <li>
            <strong>Pro subscriptions</strong> — an optional $4/month upgrade removes ads and
            unlocks early access to sponsor deals.
          </li>
          <li>
            <strong>Sponsored retailer slots</strong> — retailers can pay to boost a deal to the
            top of the feed or feature it as our Deal of the Day. Sponsored deals are always
            clearly labeled.
          </li>
          <li>
            <strong>Display advertising</strong> — served through Google AdSense. Ads are
            personalized based on your browsing; you can opt out at{" "}
            <a
              href="https://www.google.com/settings/ads"
              target="_blank"
              rel="noopener nofollow"
              className="underline"
            >
              google.com/settings/ads
            </a>
            .
          </li>
        </ul>

        <h2 className="serif text-2xl mt-8">Our editorial standards</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Every code is checked against the retailer&apos;s current promo page before publish.</li>
          <li>Community votes surface working codes and bury dead ones automatically.</li>
          <li>Sponsored placements are labeled with a &quot;Sponsored&quot; badge — no exceptions.</li>
          <li>We never accept payment to hide a competing deal or downrank a rival retailer.</li>
        </ul>

        <h2 className="serif text-2xl mt-8">Where we&apos;re headed</h2>
        <p>
          Long-term, The Couponer will always be free for shoppers. New features on the roadmap:
          browser extension for auto-apply codes at checkout, ZIP-based localized grocery deals,
          weekly cashback rebates for Pro users, and a Slack/Discord alerts channel for extreme
          markdowns.
        </p>

        <h2 className="serif text-2xl mt-8">Get in touch</h2>
        <p>
          Story tip, deal correction, press inquiry, or partnership? Head over to our{" "}
          <Link to="/contact" className="underline">
            contact page
          </Link>{" "}
          or email{" "}
          <a href={`mailto:${CONTACT_EMAIL}`} className="underline">
            {CONTACT_EMAIL}
          </a>
          .
        </p>

        <p className="text-sm text-[#5c5c5c] mt-10">
          Read the fine print:{" "}
          <Link to="/privacy" className="underline">
            Privacy Policy
          </Link>{" "}
          ·{" "}
          <Link to="/terms" className="underline">
            Terms of Service
          </Link>
        </p>
      </div>
    </div>
  );
}
